import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import Image from 'next/image';
import { IconArrow } from '../atoms/Icons';

/**
 * Reusable card component with consistent layout and link behavior
 * Used across the app for events, attractions, articles, etc.
 *
 * @param {Object} props
 * @param {string} [props.href] - Destination URL. Internal paths (/...) use Next.js <Link> with prefetch when featured. External URLs open in new tab. Omit for non-clickable cards.
 * @param {string} props.title - Card title (rendered as <h2>)
 * @param {ReactNode} props.content - Left-side visual content (e.g. date badge, thumbnail, icon)
 * @param {ReactNode[]} [props.metas] - Array of meta information rows (usually <Meta /> components)
 * @param {boolean} [props.featured=false] - When true and href is internal, enables prefetching on <Link>
 * @param {string} [props.detailsLabel] - Optional label above the arrow button (e.g., "Heart Details")
 * @param {ReactNode} [props.leadingActions] - Optional elements rendered before the details label in the action area
 * @param {ReactNode} [props.rightColumn] - Optional custom content for the right column area. When provided, replaces the default action area.
 */
export default function BaseCard(t0) {
  const $ = _c(37);
  const {
    href,
    title,
    content,
    metas: t1,
    featured: t2,
    detailsLabel,
    leadingActions: t3,
    onClick,
    showActionArea: t4,
    metasDirection: t5,
    isActive: t6,
    rightColumn: t7
  } = t0;
  let t8;
  if ($[0] !== t1) {
    t8 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const metas = t8;
  const featured = t2 === undefined ? false : t2;
  const leadingActions = t3 === undefined ? null : t3;
  const showActionArea = t4 === undefined ? true : t4;
  const metasDirection = t5 === undefined ? "responsive" : t5;
  const isActive = t6 === undefined ? false : t6;
  const rightColumn = t7 === undefined ? null : t7;
  const anchorClasses = `
        block ${isActive ? "bg-[#ECECEC]" : "bg-white"} shadow-[-2px_2px_20px_0_rgba(0,0,0,0.06)]
        focus:outline-none mb-2 rounded-[14px]
        focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2
    `;
  const metasDirectionClass = metasDirection === "row" ? "block" : "flex";
  const t9 = `grid ${metasDirection !== "row" ? "lg:p-0 p-2 md:px-2" : "p-[7px]"} ${showActionArea || rightColumn ? "grid-cols-[auto_1fr_auto]" : "grid-cols-[auto_1fr]"} md:items-center items-end lg:gap-4 gap-3 relative`;
  const t10 = `${metasDirection !== "row" ? "lg:py-4" : ""} self-center`;
  let t11;
  if ($[2] !== title) {
    t11 = <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2"><h2 className="text-base leading-[1.2em] font-semibold tracking-tight text-[#323232] overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]">{title}</h2></div>;
    $[2] = title;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  let t12;
  if ($[4] !== metas || $[5] !== metasDirectionClass) {
    t12 = metas.length > 0 && <div className={`items-start flex-wrap md:flex-row flex-col ${metasDirectionClass} xl:gap-4 md:gap-3 gap-1.5 text-[13px] leading-[1.23em] text-[#626262]`}>{metas}</div>;
    $[4] = metas;
    $[5] = metasDirectionClass;
    $[6] = t12;
  } else {
    t12 = $[6];
  }
  let t13;
  if ($[7] !== t10 || $[8] !== t11 || $[9] !== t12) {
    t13 = <div className={t10}>{t11}{t12}</div>;
    $[7] = t10;
    $[8] = t11;
    $[9] = t12;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] !== detailsLabel || $[12] !== leadingActions || $[13] !== rightColumn || $[14] !== showActionArea) {
    t14 = rightColumn ? <div className="flex justify-end">{rightColumn}</div> : showActionArea && <div className="hidden lg:flex items-center xl:gap-3 gap-2 lg:mr-7">{leadingActions}{detailsLabel && <span className="xl:text-sm text-[13px] md:block hidden text-[#3232322] font-medium max-w-[90px] leading-[1.14em] text-right whitespace-normal">{detailsLabel.split(" ").map(_temp)}</span>}<span aria-hidden="true" className="grid xl:h-9 xl:w-9 w-6 h-6 place-items-center rounded-full bg-[#00EAFD] text-black"><IconArrow className="xl:w-5 w-3" /></span></div>;
    $[11] = detailsLabel;
    $[12] = leadingActions;
    $[13] = rightColumn;
    $[14] = showActionArea;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== content || $[17] !== t13 || $[18] !== t14 || $[19] !== t9) {
    t15 = <div className={t9}>{content}{t13}{t14}</div>;
    $[16] = content;
    $[17] = t13;
    $[18] = t14;
    $[19] = t9;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const inner = t15;
  if (!href) {
    let t16;
    if ($[21] !== anchorClasses || $[22] !== inner || $[23] !== onClick || $[24] !== title) {
      t16 = <div className={anchorClasses} role="group" aria-label={title} onClick={onClick}>{inner}</div>;
      $[21] = anchorClasses;
      $[22] = inner;
      $[23] = onClick;
      $[24] = title;
      $[25] = t16;
    } else {
      t16 = $[25];
    }
    return t16;
  }
  const isInternal = href.startsWith("/");
  if (isInternal) {
    let t16;
    if ($[26] !== anchorClasses || $[27] !== featured || $[28] !== href || $[29] !== inner || $[30] !== onClick) {
      t16 = <Link href={href} prefetch={featured} className={anchorClasses} onClick={onClick}>{inner}</Link>;
      $[26] = anchorClasses;
      $[27] = featured;
      $[28] = href;
      $[29] = inner;
      $[30] = onClick;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    return t16;
  }
  let t16;
  if ($[32] !== anchorClasses || $[33] !== href || $[34] !== inner || $[35] !== onClick) {
    t16 = <a href={href} target="_blank" rel="noopener noreferrer" className={anchorClasses} onClick={onClick}>{inner}</a>;
    $[32] = anchorClasses;
    $[33] = href;
    $[34] = inner;
    $[35] = onClick;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}

/**
 * Small meta row with icon (pin, clock, directions, etc.)
 * Pure component — icons are passed from outside. *
 * @param {Object} props
 * @param {string | StaticImageData} props.iconSrc
 * @param {string} [props.alt]
 * @param {number} [props.width=10]
 * @param {number} [props.height=12]
 * @param {string} [props.className="w-3 h-auto"]
 * @param {ReactNode} props.children
 */
function _temp(word, index) {
  return <span key={index} className="block">{word}</span>;
}
export function Meta(t0) {
  const $ = _c(12);
  const {
    iconSrc,
    alt: t1,
    width: t2,
    height: t3,
    className: t4,
    children,
    metasDirection: t5
  } = t0;
  const alt = t1 === undefined ? "" : t1;
  const width = t2 === undefined ? 10 : t2;
  const height = t3 === undefined ? 12 : t3;
  const className = t4 === undefined ? "w-3 h-auto" : t4;
  const metasDirection = t5 === undefined ? "responsive" : t5;
  const mapViewListDetail = metasDirection === "row" ? "md:max-w-[152px] sm:max-w-[180px] max-w-[90px]" : "";
  const t6 = `flex items-start gap-1 mb-[2px] ${mapViewListDetail}`;
  let t7;
  if ($[0] !== alt || $[1] !== className || $[2] !== height || $[3] !== iconSrc || $[4] !== width) {
    t7 = iconSrc && <span className="flex items-center justify-center w-4 h-4 shrink-0"><Image src={iconSrc} alt={alt} width={width} height={height} className={className} /></span>;
    $[0] = alt;
    $[1] = className;
    $[2] = height;
    $[3] = iconSrc;
    $[4] = width;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== children) {
    t8 = <span className="overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]">{children}</span>;
    $[6] = children;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t6 || $[9] !== t7 || $[10] !== t8) {
    t9 = <div className={t6}>{t7}{t8}</div>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}